import * as Sentry from "@sentry/browser";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { Address } from "viem";
import { getNetwork, readContract } from "wagmi/actions";
import { BALANCES_ALLOWANCES_CONTRACT_ABI } from "../../config/abis/BalancesAllowancesContract.abi";
import {
  BALANCE_CONTRACTS,
  PERMIT_TWO_CONTRACT_ADDRESS,
  SENTRY_CONTEXTS,
} from "../../config/consts";
import { Amount } from "../../types/amount";
import { GlobalStore } from "./GlobalStore";
import { Token } from "./TokenEnum";
import { TokenList } from "./getAllTokenLists";
import { getTokenDecimals } from "../../utils/tokenUtils";

export interface Allowance {
  symbol: Token;
  allowance: Amount;
}

export type Allowances = Array<Allowance>;
const fetchPermit2Allowances = (
  address: Address,
  tokenList: TokenList | undefined
): Promise<Array<{ symbol: Token; allowance: Amount }>> => {
  const { chain } = getNetwork();
  if (chain && tokenList) {
    const tokenSymbols = Object.keys(tokenList) as Array<Token>;
    const tokensAddresses = tokenSymbols.map(symbol => {
      return tokenList[symbol]?.chainInfo.contractAddress;
    }) as Array<`0x${string}`>;
    return readContract({
      address: BALANCE_CONTRACTS[chain.id],
      abi: BALANCES_ALLOWANCES_CONTRACT_ABI,
      functionName: "walletAllowances",
      args: [address, PERMIT_TWO_CONTRACT_ADDRESS, tokensAddresses],
    })
      .then(res => {
        const allowances = res.map((allowanceBigNum, index) => {
          const tokenDecimals = getTokenDecimals(tokenSymbols[index], tokenList);
          const allowance = new Amount(allowanceBigNum.toString()).div(
            new Amount((10 ** tokenDecimals).toString())
          );
          return {
            symbol: tokenSymbols[index],
            allowance: allowance,
          };
        });
        return allowances;
      })
      .catch(err => {
        console.error(`Allowances error`, err);
        return [];
      });
  }
  return Promise.reject("Invalid or no chain");
};

export const getPermit2Allowances = createAsyncAction(
  async ({ address, tokenList }: { address: `0x${string}`; tokenList: TokenList | undefined }) => {
    try {
      Sentry.configureScope(scope => {
        scope.setTransactionName(SENTRY_CONTEXTS.ALLOWANCES);
      });
      const result = await fetchPermit2Allowances(address, tokenList);
      if (result && result.length > 0) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not get allowances`, { extra: { event: e } });
      return errorResult([], `Could not get allowances`, e);
    }
    Sentry.captureException(`Could not get allowances`);
    return errorResult([], `Couldn't get allowances`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error) {
        GlobalStore.update(s => {
          s.allowances = result.payload;
        });
      }
    },
  }
);
